import type { ReactNode } from 'react'
import { Cog } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { SITE_FAMILIES, type NavEntry, type NavFamily } from './site-navigation.js'
import type { ShellActive } from '../admin/Shell.js'

/**
 * SHELL DE MODULE NON CONFIGURÉ (IA V2, §117) — la vraie page d'une
 * fonction métier dont le connecteur n'est pas encore branché. Elle dit ce
 * que le module FERA, quel fournisseur est pressenti, et où le configurer.
 * AUCUNE fausse donnée : pas de liste vide qui prétend contenir 0 éléments.
 */

const VISIONS: Record<string, ReactNode> = {
  'controle-acces': (
    <>
      <p>
        Cette page réunira toutes les <strong>identités d’accès</strong> du
        club — salariées ou non : un DJ invité, un prestataire de ménage ou
        un technicien extérieur peuvent avoir un badge sans être des
        employés. Chaque identité portera ses identifiants (badges, Mobile
        Wallet), ses groupes, ses zones et horaires, et son activité
        (passages, refus).
      </p>
      <p>
        <strong>Le contrôle d’accès n’est pas la RH.</strong> Une identité
        d’accès peut être <em>liée</em> à une personne de Gestion — c’est
        une liaison optionnelle, jamais une synchronisation : une fin de
        contrat ne supprimera jamais un accès en silence, la désactivation
        sera toujours une décision explicite.
      </p>
    </>
  ),
  videosurveillance: (
    <p>
      Cette page donnera accès aux caméras du club et à leurs
      enregistrements : mosaïque, état des caméras, recherche dans
      l’historique — en lecture, avec les règles de rétention du club.
    </p>
  ),
  reseau: (
    <p>
      Cette page montrera le réseau du club : bornes Wi-Fi, VLAN, clients
      connectés, santé des liens — la fonction métier reste « Réseau »,
      quel que soit le fournisseur derrière.
    </p>
  ),
}

export function ModuleShellPage({ moduleId }: { moduleId: string }) {
  const meta = useShellMeta()
  let fam: NavFamily | null = null
  let entry: NavEntry | null = null
  for (const f of SITE_FAMILIES) {
    const c = f.children.find((x) => x.id === moduleId)
    if (c) { fam = f; entry = c; break }
  }
  if (!fam || !entry) return null

  return (
    <Shell active={fam.id as ShellActive} meta={meta}>
      <main className="lc-page lc-stagger fh-page">
        <Link className="lc-crumb" href={fam.route}>← {fam.label}</Link>
        <header className="fh-hero">
          <span className="fh-hero__icon" aria-hidden="true"><entry.icon size={26} /></span>
          <div>
            <h1 className="lc-title">{entry.label}</h1>
            <p className="lc-subtitle">
              {entry.description}{' '}
              <span className="lc-pill lc-pill--idle">Non configuré</span>
            </p>
          </div>
        </header>

        <section className="lc-panel fh-shellpanel">
          <div className="lc-panel__body fh-vision">
            {VISIONS[moduleId] ?? <p>{entry.description}</p>}
            {entry.provider ? (
              <p className="lc-hint">
                Fournisseur pressenti : <strong>{entry.provider}</strong> —
                le module restera « {entry.label} » quel que soit le système
                branché derrière.
              </p>
            ) : null}
            <div className="fh-vision__cta">
              <Link href="/settings/integrations" className="lc-btn lc-btn--primary">
                <Cog size={14} aria-hidden="true" /> Configurer une intégration
              </Link>
            </div>
          </div>
        </section>
      </main>
    </Shell>
  )
}
